import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ExternalLink, Key, Save, Trash2, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApiCredentialBox({ targetName, token, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/syndication/credentials/${encodeURIComponent(targetName)}`, { headers });
      setData(res.data);
      setValues(res.data.values_masked || {});
    } catch (e) {
      // 404 = no schema, just show message
      setData({ error: e.response?.data?.detail || 'Failed to load' });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && !data) load(); }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/syndication/credentials/${encodeURIComponent(targetName)}`, { values }, { headers });
      toast.success(`${targetName} credentials saved`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!confirm(`Clear all stored credentials for ${targetName}?`)) return;
    try {
      await axios.delete(`${API}/syndication/credentials/${encodeURIComponent(targetName)}`, { headers });
      toast.success('Cleared');
      setValues({});
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="border border-dashed border-border rounded-lg p-3 mt-2 bg-muted/20" data-testid={`api-cred-box-${targetName.replace(/\W+/g, '-').toLowerCase()}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid={`api-cred-toggle-${targetName.replace(/\W+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-2">
          <Key className="h-3.5 w-3.5 text-indigo-400" />
          <span className="text-xs font-semibold">API Credentials</span>
          {data?.is_configured ? (
            <Badge variant="outline" className="text-[10px] bg-emerald-500/10 border-emerald-500/30 text-emerald-500">
              <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Configured
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] bg-amber-500/10 border-amber-500/30 text-amber-500">
              <AlertCircle className="h-2.5 w-2.5 mr-0.5" /> Not connected
            </Badge>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground">{open ? 'Hide' : 'Add / Edit'}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`api-cred-panel-${targetName.replace(/\W+/g, '-').toLowerCase()}`}>
          {loading && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>}

          {data?.error && (
            <div className="text-xs text-rose-400 flex items-center gap-1.5">
              <AlertCircle className="h-3 w-3" /> {data.error}
            </div>
          )}

          {data?.fields && (
            <>
              {data.instructions && (
                <p className="text-[11px] text-muted-foreground leading-relaxed">{data.instructions}</p>
              )}

              {data.signup_url && (
                <a
                  href={data.signup_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:underline"
                  data-testid={`api-cred-signup-${targetName.replace(/\W+/g, '-').toLowerCase()}`}
                >
                  <ExternalLink className="h-3 w-3" /> Get API credentials →
                </a>
              )}

              <div className="space-y-2 pt-1">
                {data.fields.map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-[11px] flex items-center gap-1">
                      {f.label} {f.required && <span className="text-rose-400">*</span>}
                    </Label>
                    <Input
                      type={f.type === 'password' ? 'password' : 'text'}
                      value={values[f.key] ?? ''}
                      onChange={e => setValues(p => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.help || `Paste your ${f.label.toLowerCase()}`}
                      className="h-8 text-xs font-mono"
                      data-testid={`api-cred-field-${targetName.replace(/\W+/g, '-').toLowerCase()}-${f.key}`}
                    />
                    {f.help && <p className="text-[10px] text-muted-foreground">{f.help}</p>}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" onClick={save} disabled={saving} className="text-xs bg-indigo-600 hover:bg-indigo-700" data-testid={`api-cred-save-${targetName.replace(/\W+/g, '-').toLowerCase()}`}>
                  {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving…</> : <><Save className="h-3 w-3 mr-1" /> Save</>}
                </Button>
                {data.is_configured && (
                  <Button size="sm" variant="outline" onClick={clear} className="text-xs text-rose-500 border-rose-500/30 hover:bg-rose-500/10" data-testid={`api-cred-clear-${targetName.replace(/\W+/g, '-').toLowerCase()}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Clear
                  </Button>
                )}
                {data.updated_at && (
                  <span className="text-[10px] text-muted-foreground ml-auto">
                    Updated {new Date(data.updated_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
